import {
  isMissionPathReferenceSelected,
  useDataContext,
  useFeatureExtractionContext,
  useFxWorkspaceContext,
  type FxWorkspace,
  type FxWorkspaceLoadStatus,
} from '@mobile-mapping/providers';
import { LoadState } from '@trimble/data-loading';
import { Accessor, createMemo } from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';

import {
  applyFxReferencesFilter,
  type FxReferencesFilterMode,
} from './filter-fx-reference-nodes';
import {
  flattenFxReferenceNodes,
  type FxReferencesViewMode,
} from './flatten-fx-reference-nodes';
import {
  collectSelectedMissionIds,
  collectVisibleReferenceKeys,
  matchesFxReferenceFilter,
  type FxReferenceFilterVisibility,
  type FxReferenceVisibilityReaders,
} from './fx-reference-filter-visibility';
import {
  buildFxWorkspaceNodesForActive,
  FxWorkspaceProjectData,
  FxWorkspaceTreeItem,
} from './fx-workspace-tree-nodes';
import { isProjectDataLoadItemVisible } from './project-data-load-control';

type ChildrenLoadStatus = 'pending' | 'success' | 'error';

const getFxWorkspacesChildrenLoadStatus = (
  status: FxWorkspaceLoadStatus
): ChildrenLoadStatus => {
  switch (status) {
    case 'idle':
    case 'pending':
      return 'pending';
    case 'error':
      return 'error';
    case 'success':
      return 'success';
    default:
      throw new UnreachableCaseError(status);
  }
};

export interface UseActiveFxWorkspaceTreeOptions {
  referencesFilterMode?: Accessor<FxReferencesFilterMode>;
  referencesViewMode?: Accessor<FxReferencesViewMode>;
}

export interface UseActiveFxWorkspaceTreeResult {
  activeWorkspace: Accessor<FxWorkspace | undefined>;
  nodes: Accessor<TreeNodeDefinition<FxWorkspaceTreeItem>[]>;
  childrenLoadStatus: Accessor<ChildrenLoadStatus>;
}

export function useActiveFxWorkspaceTree(
  options: UseActiveFxWorkspaceTreeOptions = {}
): UseActiveFxWorkspaceTreeResult {
  const fxWorkspace = useFxWorkspaceContext();
  const { dataState } = useDataContext();
  const featureExtraction = useFeatureExtractionContext();

  const projectData = createMemo((): FxWorkspaceProjectData => {
    const missions =
      dataState.mission.state === LoadState.LoadComplete
        ? Object.values(dataState.mission.items)
        : [];
    const trajectories =
      dataState.trajectory.state === LoadState.LoadComplete
        ? Object.values(dataState.trajectory.items)
        : [];
    const models =
      dataState.tcFileFolder.state === LoadState.LoadComplete
        ? Object.values(dataState.tcFileFolder.items)
        : [];
    const pointClouds =
      dataState.pointCloud.state === LoadState.LoadComplete
        ? Object.values(dataState.pointCloud.items)
        : [];
    const showMapWorkspaces =
      featureExtraction.isFeatureExtractionEnabled() &&
      featureExtraction.scope()?.projectId !== undefined;

    return {
      missions,
      trajectories,
      models,
      pointClouds,
      mapWorkspaces: showMapWorkspaces
        ? featureExtraction.editingState.workspaces
        : [],
      mapLayers: showMapWorkspaces ? featureExtraction.editingState.layers : [],
    };
  });

  const activeWorkspace = createMemo(() => {
    const activeWorkspaceId = fxWorkspace.activeWorkspaceId();
    if (activeWorkspaceId === undefined) {
      return undefined;
    }

    return fxWorkspace
      .workspaces()
      .find((workspace) => workspace.id === activeWorkspaceId);
  });

  const nodes = createMemo(() => {
    const workspace = activeWorkspace();
    if (workspace === undefined) {
      return [];
    }

    const data = projectData();
    const tree = buildFxWorkspaceNodesForActive(workspace, data);
    const viewMode = options.referencesViewMode?.() ?? 'tree';
    const viewed = viewMode === 'flat' ? flattenFxReferenceNodes(tree) : tree;
    const filterMode = options.referencesFilterMode?.() ?? 'workspace';
    const draftCounts =
      fxWorkspace.isWorkspaceDataEditing() &&
      fxWorkspace.editingWorkspaceId() === workspace.id;
    const directlySelected = (refTrn: string): boolean =>
      fxWorkspace.isReferenceVisible(workspace.id, refTrn) ||
      (draftCounts && fxWorkspace.isDraftReference(refTrn));
    const referenceSelected = (refTrn: string): boolean =>
      isMissionPathReferenceSelected(refTrn, data.missions, directlySelected);
    const readers: FxReferenceVisibilityReaders = {
      referenceSelected,
      isLayerVisible: (layerId) => featureExtraction.isLayerVisible(layerId),
      isLoadItemVisible: (type, refTrn) =>
        isProjectDataLoadItemVisible(dataState, type, refTrn),
    };
    // filterNodes calls the predicate, then applyFxReferencesFilter calls it
    // again for each remaining reference node. Read visibility once.
    const visibility: FxReferenceFilterVisibility = {
      selectedMissionIds: collectSelectedMissionIds(
        data.missions,
        referenceSelected
      ),
      ...collectVisibleReferenceKeys(viewed, filterMode, readers),
    };

    return applyFxReferencesFilter(viewed, (node) =>
      matchesFxReferenceFilter(node, filterMode, visibility)
    );
  });

  const childrenLoadStatus = createMemo(() => {
    const listStatus = getFxWorkspacesChildrenLoadStatus(
      fxWorkspace.loadStatus()
    );
    if (listStatus !== 'success') {
      return listStatus;
    }

    if (fxWorkspace.activeWorkspaceId() === undefined) {
      // Auto-activation of the first workspace happens after the list
      // succeeds. Report pending so the tab shows a spinner instead of empty.
      return fxWorkspace.workspaces().length > 0 ? 'pending' : 'success';
    }

    return getFxWorkspacesChildrenLoadStatus(
      fxWorkspace.activeWorkspaceDetailsLoadStatus()
    );
  });

  return {
    activeWorkspace,
    nodes,
    childrenLoadStatus,
  };
}
